import { UseQueryResult, useQuery } from '@tanstack/react-query';
import {
  GeneralConfigProfileDto,
  PrintingDefaultDto,
  RelationshipListItemDto
} from 'src/models/relationshipmanagement';
import {
  getCustomersProfiles,
  getGeneralConfigProfileById,
  getPrintingDefaults,
  getPrintingTypes,
  getRelationshipView
} from 'src/services/relationshipmanagement';
import { ConsignmentSettingsGroupDetails, PrintingType } from './types';
import { getAccountOnboarded, getAccountSubscriptionsByAccountId, getNetworks } from 'src/services/account';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { QueryProps } from 'src/queries/baseQueryFunctions';
import { GetLabelQueryParams } from 'src/models/routedetermination';
import { getLabels } from 'src/services/routedetermination';
import { isPalletlinePrintingType } from './helpers';

const getConsignmentSettingsGroupDetails = async (
  id?: string,
  tenantId?: string,
  labelParams?: GetLabelQueryParams
): Promise<ConsignmentSettingsGroupDetails> => {
  const allRelationships = getCustomersProfiles();
  const printingTypes = getPrintingTypes();
  const labelsQuery = labelParams ? getLabels(labelParams) : Promise.resolve({ data: [] });

  const res = await Promise.all([allRelationships, printingTypes, labelsQuery]);

  let consignmentSettingsGroup: GeneralConfigProfileDto = {};
  let printingDefaults: PrintingDefaultDto[] = [];
  let assignedRelationships: RelationshipListItemDto[] = [];

  if (id) {
    consignmentSettingsGroup = (await getGeneralConfigProfileById(id)).data;
    printingDefaults = (await getPrintingDefaults({ filter: `generalConfigProfileId eq guid'${id}'` })).data;
    assignedRelationships = (
      await getRelationshipView({ filter: `generalConfigProfileId eq guid'${id}'`, sort: 'accountCode' })
    ).data;
  }

  const printingTypesList: PrintingType[] = [];
  for (let index = 0; index < res[1].data.length; index++) {
    const isContradoLabel = isPalletlinePrintingType(res[1].data[index].code);
    printingTypesList.push({
      ...res[1].data[index],
      // Palletline stays hidden unless the tenant's subscription check below passes
      isVisible: !isContradoLabel,
      isContradoLabel
    });
  }

  const contradoLabelType = printingTypesList.find(x => x.isContradoLabel);
  if (tenantId && contradoLabelType) {
    const isAccountOnboarded = (await getAccountOnboarded(tenantId)).data;

    if (isAccountOnboarded) {
      const accountSubscriptions = (await getAccountSubscriptionsByAccountId(tenantId)).data;
      const networkConnectSubscriptions = accountSubscriptions.filter(x => x.product?.isNetworkConnect);

      if (networkConnectSubscriptions.length > 0) {
        const palletlineNetwork = (await getNetworks({ filter: 'isPalletline eq true' })).data;
        const palletlineNetworkId = palletlineNetwork[0]?.id;

        if (
          palletlineNetworkId &&
          networkConnectSubscriptions.some(x => x.product?.networkId === palletlineNetworkId)
        ) {
          contradoLabelType.isVisible = true;
          contradoLabelType.networkId = palletlineNetworkId;
        }
      }
    }
  }

  return {
    allRelationships: res[0].data,
    printingTypes: printingTypesList,
    consignmentSettingsGroup: consignmentSettingsGroup,
    printingDefaults: printingDefaults,
    assignedRelationships: assignedRelationships,
    labels: res[2].data
  };
};

/**
 * Consignment Settings Group Details Query
 */
export const useConsignmentSettingsGroupDetailsQuery = (
  id?: string,
  tenantId?: string,
  options: QueryProps<ConsignmentSettingsGroupDetails, {}> & { labelParams?: GetLabelQueryParams } = {}
): UseQueryResult<ConsignmentSettingsGroupDetails> => {
  const {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    queryKeyOverride,
    labelParams
  } = options;

  return useQuery({
    queryKey: queryKeyOverride ?? ['generalConfigProfileDetails', id, tenantId, labelParams],
    queryFn: () => getConsignmentSettingsGroupDetails(id, tenantId, labelParams),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};
